import React, { useState } from 'react';
import { CSSProperties } from 'react';
import { P3 } from 'styleguide/Texts';
import GoogleLoginImage from 'assets/google-login.svg';
import AppleLoginImage from 'assets/apple-login.svg';
import { Theme } from 'styleguide/types';
import styled, { ThemeProps } from 'styled-components';
import { colors } from 'styleguide/colors';
import { CornerRadius } from 'styleguide/spacing';
import fonts from 'styleguide/fonts';
import { themeState } from 'styleguide/theme';
import { t } from 'translations/t';

const Button = styled.button`
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-style: none;
  border-radius: ${CornerRadius.lg}px;
  height: 48px;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.greyLight : colors.black};
`;

interface LoginWithGoogleButtonProps {
  action: SocialButtonAction;
  style?: CSSProperties;
  onClick: () => void;
}

export const LoginWithGoogleButton = ({ action, style, onClick }: LoginWithGoogleButtonProps) => {
  return <SocialLoginButton action={action} style={style} type="google" onClick={onClick} />;
};

type SocialButtonType = 'google' | 'apple';
type SocialButtonAction = 'login' | 'signup';

interface SocialLoginButtonProps {
  type: SocialButtonType;
  action: SocialButtonAction;
  onClick: () => void;
  style?: CSSProperties;
}

export const SocialLoginButton = ({ type, action, onClick, style }: SocialLoginButtonProps) => {
  const ImageSVG = image(type);
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Button
      style={{
        ...style,
        ...(isHovering && {
          backgroundColor:
            themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyDark,
        }),
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div style={{ paddingInlineStart: 10, height: 30, width: 30, position: 'absolute', left: 0 }}>
        <ImageSVG />
      </div>

      <P3
        style={{
          fontWeight: fonts.medium.weight,
          fontSize: 17,
        }}
      >
        {title(type, action)}
      </P3>
    </Button>
  );
};

const image = (type: SocialButtonType) => {
  switch (type) {
    case 'google':
      return GoogleLoginImage;
    case 'apple':
      return AppleLoginImage;
  }
};

const title = (type: SocialButtonType, action: SocialButtonAction) => {
  if (action === 'login') {
    switch (type) {
      case 'google':
        return t('web.socialLoginButton.loginWithGoogle');
      case 'apple':
        return t('web.socialLoginButton.loginWithApple');
    }
  } else if (action === 'signup') {
    switch (type) {
      case 'google':
        return t('web.socialLoginButton.signInWithGoogle');
      case 'apple':
        return t('web.socialLoginButton.signInWithApple');
    }
  }
};
